import { createReducer } from '@reduxjs/toolkit';
import { LabelCategory, LabelDto } from '../../api/ClientApi';
import { myAssert } from '../../common/console';

import {
    createNewEmptyLabel,
    createNewArticleLabel,
    updateLabelTitle,
    clearLabels,
    removeLabelEntity,
    addNewLabelEntity,
    setLabelEntities,
} from '../actions/labelActions';
import { clearBaseEntityState, IBaseEntityState, SaveEntityStatus } from './common';

export type LabelEntity = {
    index: number;
    label?: LabelDto;
};

export interface LabelsFormReducerState extends IBaseEntityState {
    labelEntities: LabelEntity[] | null;
    currentLabel: LabelDto | null;
}

function clearState(state: LabelsFormReducerState): void {
    state.labelEntities = null;
    state.currentLabel = null;

    clearBaseEntityState(state);
}

function getInitialState(): LabelsFormReducerState {
    const state: LabelsFormReducerState = {
        labelEntities: null,
        currentLabel: null,
        baseJsonString: null,
        currentJsonString: null,
        isChanged: undefined,
        saveStatus: SaveEntityStatus.None,
        modified: undefined,
    };

    clearState(state);

    return state;
}

export const labelsFormReducer = createReducer<LabelsFormReducerState>(
    getInitialState(), // Initial state
    (builder) => {
        builder
            .addCase(createNewEmptyLabel, (state: LabelsFormReducerState, action: any) => {
                state.currentLabel = new LabelDto();
            })
            .addCase(createNewArticleLabel, (state: LabelsFormReducerState, action: any) => {
                state.currentLabel = new LabelDto({ labelCategory: LabelCategory.Article });
            })
            .addCase(updateLabelTitle, (state: LabelsFormReducerState, action: any) => {
                myAssert(() => state.labelEntities !== null, 'There needs to be goods if it is being updated');

                const title = action.payload;

                const oldLabel = new LabelDto();
                oldLabel.init(state.currentLabel);
                if (oldLabel !== null) {
                    oldLabel.title = title;
                    state.currentLabel = oldLabel;
                }
            })
            .addCase(addNewLabelEntity, (state: LabelsFormReducerState, action: any) => {
                const label: LabelDto = action.payload;

                const oldLabel = state.labelEntities?.find(
                    (le) => le.label?.title?.toLowerCase().trim() === label.title?.toLowerCase().trim(),
                );

                if (!oldLabel && !!label.title && label.title.trim() !== '') {
                    const index =
                        !!state.labelEntities && state.labelEntities.length > 0
                            ? state.labelEntities[state.labelEntities.length - 1].index + 1
                            : 1;

                    const newLabel: LabelEntity = {
                        index: index,
                        label: label,
                    };

                    if (!state.labelEntities) {
                        state.labelEntities = [newLabel];
                    } else {
                        state.labelEntities = [...state.labelEntities, newLabel];
                    }

                    if (label.labelCategory === LabelCategory.Article) {
                        state.currentLabel = new LabelDto({ labelCategory: LabelCategory.Article });
                    } else {
                        state.currentLabel = new LabelDto();
                    }
                }
            })
            .addCase(setLabelEntities, (state: LabelsFormReducerState, action: any) => {
                const newLabelEntities = action.payload;

                state.labelEntities = newLabelEntities ?? [];
            })
            .addCase(clearLabels, (state: LabelsFormReducerState, action: any) => {
                clearState(state);
            })
            .addCase(removeLabelEntity, (state: LabelsFormReducerState, action: any) => {
                if (!!state.labelEntities) {
                    const index = action.payload;
                    if (state.labelEntities.length === 0) {
                        state.labelEntities = [];
                    } else {
                        const labelIndex = state.labelEntities.findIndex((g) => g.index === index);
                        if (labelIndex > -1) {
                            state.labelEntities.splice(labelIndex, 1);
                        }
                    }
                }
            });
    },
);
